import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, Search } from 'lucide-react';
import { useRef } from 'react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useDebounce } from '../../hooks/useDebounce';
import { partnershipsApiService } from '../../services/partnershipsApi';
import { PartnershipCreator, PLATFORM_TYPES, PlatformType } from '../../types/partnership';
interface CreateModpackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (id?: string) => void;
  preselectedCreatorId?: string;
}

export const CreateModpackModal = ({
  isOpen,
  onClose,
  onSuccess,
  preselectedCreatorId,
}: CreateModpackModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [creatorId, setCreatorId] = useState(preselectedCreatorId || '');
  const [creatorSearch, setCreatorSearch] = useState('');
  const [creatorResults, setCreatorResults] = useState<PartnershipCreator[]>([]);
  const [selectedCreator, setSelectedCreator] = useState<PartnershipCreator | null>(null);
  const [isSearchingCreators, setIsSearchingCreators] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [platformType, setPlatformType] = useState<PlatformType | ''>('');
  const [platformUrl, setPlatformUrl] = useState('');
  const [platformSlug, setPlatformSlug] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [playerCountEstimate, setPlayerCountEstimate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedCreatorSearch = useDebounce(creatorSearch, 300);

  useEffect(() => {
    if (!debouncedCreatorSearch || debouncedCreatorSearch.length < 1) {
      setCreatorResults([]);
      return;
    }
    setIsSearchingCreators(true);
    partnershipsApiService
      .searchCreators(debouncedCreatorSearch)
      .then(setCreatorResults)
      .catch(console.error)
      .finally(() => setIsSearchingCreators(false));
  }, [debouncedCreatorSearch]);

  // Load preselected creator
  useEffect(() => {
    if (preselectedCreatorId) {
      partnershipsApiService
        .getCreatorById(preselectedCreatorId)
        .then((c) => {
          setSelectedCreator(c);
          setCreatorId(c.id);
        })
        .catch(console.error);
    }
  }, [preselectedCreatorId]);

  if (!isOpen) return null;

  const handleSelectCreator = (creator: PartnershipCreator) => {
    setSelectedCreator(creator);
    setCreatorId(creator.id);
    setCreatorSearch('');
    setCreatorResults([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!creatorId) {
      setError('Please select a creator');
      return;
    }
    if (!name.trim()) {
      setError('Modpack name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await partnershipsApiService.createModpack({
        creator_id: creatorId,
        name: name.trim(),
        description: description.trim() || undefined,
        platform_type: platformType || undefined,
        platform_url: platformUrl.trim() || undefined,
        platform_slug: platformSlug.trim() || undefined,
        logo_url: logoUrl.trim() || undefined,
        player_count_estimate: playerCountEstimate ? Number(playerCountEstimate) : undefined,
      });
      onSuccess(result.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create modpack');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div ref={modalRef} className="modal-panel max-w-lg">
        <button onClick={onClose} className="absolute top-3 right-3 text-glass-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-4">New Modpack</h2>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Creator selector */}
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Creator *</label>
            {selectedCreator ? (
              <div className="flex items-center justify-between p-2 rounded-lg bg-accent-500/10 border border-accent-500/20">
                <div>
                  <span className="text-sm font-medium text-white">{selectedCreator.name}</span>
                  <span className="text-xs text-glass-400 ml-2">({selectedCreator.type})</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCreator(null);
                    setCreatorId('');
                  }}
                  className="text-glass-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
                <input
                  type="text"
                  value={creatorSearch}
                  onChange={(e) => setCreatorSearch(e.target.value)}
                  className="glass-input w-full pl-10 text-sm"
                  placeholder="Search creators..."
                />
                {(creatorResults.length > 0 || isSearchingCreators) && (
                  <div className="absolute top-full left-0 right-0 mt-1 glass-card border border-glass-200/20 rounded-lg overflow-hidden z-10 max-h-48 overflow-y-auto">
                    {isSearchingCreators && (
                      <div className="p-3 flex items-center justify-center">
                        <Loader className="w-4 h-4 animate-spin text-accent-400" />
                      </div>
                    )}
                    {creatorResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => handleSelectCreator(c)}
                        className="w-full text-left px-3 py-2 hover:bg-glass-200/10 text-sm text-white"
                      >
                        {c.name}
                        <span className="text-xs text-glass-400 ml-2">({c.type})</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Modpack Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="glass-input w-full text-sm"
              placeholder="All The Mods 9"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="glass-input w-full text-sm"
              placeholder="Brief modpack description..."
              rows={2}
            />
          </div>

          {/* Platform */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Platform</label>
              <select
                value={platformType}
                onChange={(e) => setPlatformType(e.target.value as PlatformType | '')}
                className="glass-input w-full text-sm"
              >
                <option value="">Select...</option>
                {PLATFORM_TYPES.map((p) => (
                  <option key={p} value={p}>
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Platform Slug</label>
              <input
                type="text"
                value={platformSlug}
                onChange={(e) => setPlatformSlug(e.target.value)}
                className="glass-input w-full text-sm"
                placeholder="all-the-mods-9"
              />
            </div>
          </div>

          {/* Platform URL */}
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Platform URL</label>
            <input
              type="url"
              value={platformUrl}
              onChange={(e) => setPlatformUrl(e.target.value)}
              className="glass-input w-full text-sm"
              placeholder="https://modrinth.com/modpack/..."
            />
          </div>

          {/* Logo + Player Count */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Logo URL</label>
              <input
                type="url"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                className="glass-input w-full text-sm"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Est. Players</label>
              <input
                type="number"
                min={0}
                value={playerCountEstimate}
                onChange={(e) => setPlayerCountEstimate(e.target.value)}
                className="glass-input w-full text-sm"
                placeholder="5000"
              />
            </div>
          </div>

          {/* Logo preview */}
          {logoUrl && (
            <div className="flex items-center gap-3">
              <img
                src={logoUrl}
                alt="Logo preview"
                className="w-12 h-12 rounded-lg object-cover bg-dark-900/50"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <span className="text-xs text-glass-400">Logo preview</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="glass-button-secondary" disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="glass-button" disabled={isSubmitting}>
              {isSubmitting ? <Loader className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Modpack
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
